import React, { useEffect, useState } from 'react';
import { Code2, ExternalLink, X } from 'lucide-react';

/**
 * Easter egg component that shows GitHub repo link
 * Triggered by keyboard shortcuts: Ctrl+U, Ctrl+Shift+I, or Cmd+Option+U
 * @returns {JSX.Element|null}
 */
const ViewSourceEasterEgg = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl+U or Cmd+U (view source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u' && !e.shiftKey) {
        e.preventDefault();
        setIsVisible(true);
      }
      
      // Ctrl+Shift+I or Cmd+Option+I (inspect)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        setIsVisible(true);
      }
      
      // Cmd+Option+U (Mac view source alternative)
      if (e.metaKey && e.altKey && e.key === 'u') {
        e.preventDefault();
        setIsVisible(true);
      }

      // Escape key to close
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Auto-hide after 8 seconds
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => setIsVisible(false)}
    >
      <div 
        className="bg-card border-2 border-accent rounded-2xl p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
              <Code2 className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h3 className="text-xl font-bold tracking-tight">View Source</h3>
              <p className="text-sm text-muted-foreground">You found the easter egg! 🎉</p>
            </div>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Curious about how this site was built? Check out the source code on GitHub.
          </p>
          
          <a
            href="https://github.com/yuan-cloud/mint-cursor-personal-site"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-accent hover:bg-accent-strong text-accent-foreground font-medium py-3 px-4 rounded-lg transition-colors duration-200 group"
          >
            <Code2 className="w-4 h-4" />
            <span>View on GitHub</span>
            <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>

          <div className="text-xs text-center text-muted-foreground pt-2 space-y-1">
            <p>Built with React, Tailwind CSS, and ❤️</p>
            <p className="font-mono">Keyboard shortcuts: Ctrl+U • Ctrl+Shift+I</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSourceEasterEgg;
